// Super Master → Servidor (pedido 2026-10-12: "painel com armazenamento,
// RAM etc. pra ver como está o servidor"). Números reais do host, lidos
// pela API em GET /api/v1/platform/server-status (ver
// gpsdrive-backend src/platform-server-status/). Atualiza sozinho a cada
// 10 s enquanto a aba está aberta.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  Activity,
  CheckCircle2,
  ChevronDown,
  Clock,
  Cpu,
  Database,
  FolderOpen,
  HardDrive,
  Loader2,
  MemoryStick,
  RefreshCw,
  Server,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { MasterShell } from "@/components/master-shell";
import { HostUsagePanel, ServerCommandsPanel } from "@/components/master-server-ops";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { platformGet } from "@/lib/api/gpsdrive-platform-client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/master/server")({
  component: () => (
    <MasterShell>
      <ServerStatusPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "Servidor — Super Master — GPS Drive" }] }),
});

interface ServiceCheck {
  name: string;
  ok: boolean;
  latencyMs: number | null;
  detail: string | null;
}

interface UsageGroup {
  name: string;
  bytes: number;
  items: number;
}

interface UsageBreakdown {
  collectedAt: string;
  database: {
    totalBytes: number;
    modules: (UsageGroup & { tables: { name: string; bytes: number; rows: number }[] })[];
  };
  storage: {
    totalBytes: number;
    groups: UsageGroup[];
    largest: { key: string; bytes: number }[];
  } | null;
}

interface ServerStatus {
  collectedAt: string;
  uptimeSeconds: number;
  cpu: { usagePercent: number; cores: number; model: string | null; loadAverage: number[] };
  memory: { totalBytes: number; usedBytes: number; availableBytes: number; cacheBytes: number };
  swap: { totalBytes: number; usedBytes: number };
  disk: { totalBytes: number; usedBytes: number; freeBytes: number };
  api: { rssBytes: number; uptimeSeconds: number; nodeVersion: string };
  services: ServiceCheck[];
}

function formatBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1).replace(".", ",")} GB`;
  if (n >= 10 * 1024 ** 2) return `${Math.round(n / 1024 ** 2)} MB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1).replace(".", ",")} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

function pct(used: number, total: number): number {
  return total > 0 ? Math.round((used / total) * 1000) / 10 : 0;
}

// Faixas: até 70% tranquilo, até 90% atenção, acima disso crítico.
function level(percent: number): "ok" | "warn" | "crit" {
  if (percent >= 90) return "crit";
  if (percent >= 70) return "warn";
  return "ok";
}

const BAR_COLOR = { ok: "bg-emerald-500", warn: "bg-amber-500", crit: "bg-red-500" } as const;
const TEXT_COLOR = {
  ok: "text-emerald-600 dark:text-emerald-400",
  warn: "text-amber-600 dark:text-amber-400",
  crit: "text-red-600 dark:text-red-400",
} as const;

function ServerStatusPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["platform", "server-status"],
    queryFn: () => platformGet<ServerStatus>("/api/v1/platform/server-status"),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const memPct = data ? pct(data.memory.usedBytes, data.memory.totalBytes) : 0;
  const diskPct = data ? pct(data.disk.usedBytes, data.disk.totalBytes) : 0;
  const swapPct = data ? pct(data.swap.usedBytes, data.swap.totalBytes) : 0;
  const loadPct = data ? pct(data.cpu.loadAverage[0] ?? 0, data.cpu.cores) : 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Servidor</h1>
          <p className="text-sm text-muted-foreground">
            Uso de processador, memória e disco do servidor, e se cada serviço está respondendo.
            Atualiza a cada 10 segundos.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn("mr-2 h-4 w-4", isFetching && "animate-spin")} />
          Atualizar
        </Button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Visão geral</TabsTrigger>
          <TabsTrigger value="usage">O que mais gasta</TabsTrigger>
          <TabsTrigger value="commands">Comandos</TabsTrigger>
        </TabsList>
        <TabsContent value="usage" className="space-y-6 pt-2">
          <HostUsagePanel />
          <UsageSection />
        </TabsContent>
        <TabsContent value="commands" className="pt-2">
          <ServerCommandsPanel />
        </TabsContent>
        <TabsContent value="overview" className="space-y-6 pt-2">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : isError || !data ? (
            <Card className="border-destructive/40">
              <CardContent className="py-6 text-sm text-destructive">
                Não foi possível ler o estado do servidor.{" "}
                {error instanceof Error ? error.message : ""}
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <GaugeCard
                  icon={Cpu}
                  title="Processador"
                  percent={data.cpu.usagePercent}
                  primary={`${data.cpu.usagePercent.toLocaleString("pt-BR")}%`}
                  secondary={`${data.cpu.cores} núcleos`}
                />
                <GaugeCard
                  icon={MemoryStick}
                  title="Memória RAM"
                  percent={memPct}
                  primary={`${formatBytes(data.memory.usedBytes)} de ${formatBytes(data.memory.totalBytes)}`}
                  secondary={`${formatBytes(data.memory.availableBytes)} livres`}
                />
                <GaugeCard
                  icon={HardDrive}
                  title="Disco"
                  percent={diskPct}
                  primary={`${formatBytes(data.disk.usedBytes)} de ${formatBytes(data.disk.totalBytes)}`}
                  secondary={`${formatBytes(data.disk.freeBytes)} livres`}
                />
                <GaugeCard
                  icon={Activity}
                  title="Memória de troca (swap)"
                  percent={swapPct}
                  primary={
                    data.swap.totalBytes > 0
                      ? `${formatBytes(data.swap.usedBytes)} de ${formatBytes(data.swap.totalBytes)}`
                      : "Sem swap"
                  }
                  secondary="Usada quando a RAM acaba — alta = servidor lento"
                />
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Serviços
                    </CardTitle>
                    <CardDescription>
                      Cada serviço é testado de verdade a cada atualização.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="divide-y rounded-md border">
                      <ServiceRow
                        service={{
                          name: "API do sistema",
                          ok: true,
                          latencyMs: null,
                          detail: `${formatBytes(data.api.rssBytes)} em uso`,
                        }}
                        extra={`no ar há ${formatUptime(data.api.uptimeSeconds)}`}
                      />
                      {data.services.map((s) => (
                        <ServiceRow key={s.name} service={s} />
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Server className="h-4 w-4 text-sky-500" /> Detalhes
                    </CardTitle>
                    <CardDescription>Informações gerais da máquina.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-0 text-sm">
                    <DetailRow label="Ligado há" value={formatUptime(data.uptimeSeconds)} />
                    <DetailRow
                      label="Carga média (1 / 5 / 15 min)"
                      value={
                        <span className={TEXT_COLOR[level(loadPct)]}>
                          {data.cpu.loadAverage.map((n) => n.toLocaleString("pt-BR")).join(" / ")}
                        </span>
                      }
                      hint={`Até ${data.cpu.cores} é normal (1 por núcleo)`}
                    />
                    <DetailRow
                      label="Memória em cache"
                      value={formatBytes(data.memory.cacheBytes)}
                      hint="Liberada sozinha quando precisa"
                    />
                    <DetailRow label="Processador" value={data.cpu.model ?? "—"} />
                    <DetailRow label="Node.js da API" value={data.api.nodeVersion} />
                    <DetailRow
                      label="Última leitura"
                      value={
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {new Date(data.collectedAt).toLocaleTimeString("pt-BR")}
                        </span>
                      }
                    />
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// "O que mais gasta" (2026-10-12): espaço por módulo no banco do sistema e
// por tipo de arquivo no storage. Cache de 5 min no servidor; "Recalcular"
// força uma leitura nova.
function UsageSection() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["platform", "server-status", "usage"],
    queryFn: () => platformGet<UsageBreakdown>("/api/v1/platform/server-status/usage"),
    staleTime: 5 * 60_000,
  });
  const recalc = useQuery({
    queryKey: ["platform", "server-status", "usage", "force"],
    queryFn: () => platformGet<UsageBreakdown>("/api/v1/platform/server-status/usage?force=1"),
    enabled: false,
  });
  const usage = recalc.data ?? data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">O que mais gasta</h2>
          <p className="text-sm text-muted-foreground">
            Quanto cada parte do sistema ocupa no banco de dados e nos arquivos guardados.
            {usage && ` Medido às ${new Date(usage.collectedAt).toLocaleTimeString("pt-BR")}.`}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => recalc.refetch()}
          disabled={isFetching || recalc.isFetching}
        >
          <RefreshCw className={cn("mr-2 h-4 w-4", recalc.isFetching && "animate-spin")} />
          Recalcular
        </Button>
      </div>

      {isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : isError || !usage ? (
        <Card className="border-destructive/40">
          <CardContent className="flex items-center justify-between gap-3 py-4 text-sm text-destructive">
            Não foi possível medir o uso agora.
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Tentar de novo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Database className="h-4 w-4 text-violet-500" /> Banco de dados por módulo
              </CardTitle>
              <CardDescription>
                Total {formatBytes(usage.database.totalBytes)}. Toque num módulo para ver as
                tabelas.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-1">
              {usage.database.modules.map((m) => (
                <ModuleRow key={m.name} module={m} max={usage.database.modules[0]?.bytes ?? 1} />
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <FolderOpen className="h-4 w-4 text-amber-500" /> Arquivos guardados
              </CardTitle>
              <CardDescription>
                {usage.storage
                  ? `Total ${formatBytes(usage.storage.totalBytes)} em fotos, documentos, APKs etc.`
                  : "Não foi possível ler o armazenamento de arquivos agora."}
              </CardDescription>
            </CardHeader>
            {usage.storage && (
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {usage.storage.groups.map((g) => (
                    <UsageBar
                      key={g.name}
                      label={g.name}
                      bytes={g.bytes}
                      max={usage.storage?.groups[0]?.bytes ?? 1}
                      hint={`${g.items.toLocaleString("pt-BR")} ${g.items === 1 ? "arquivo" : "arquivos"}`}
                    />
                  ))}
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Maiores arquivos
                  </p>
                  <ul className="divide-y rounded-md border text-xs">
                    {usage.storage.largest.slice(0, 8).map((f) => (
                      <li
                        key={f.key}
                        className="flex items-center justify-between gap-3 px-3 py-1.5"
                      >
                        <span className="truncate font-mono text-muted-foreground" title={f.key}>
                          {f.key}
                        </span>
                        <span className="shrink-0 tabular-nums">{formatBytes(f.bytes)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function UsageBar({
  label,
  bytes,
  max,
  hint,
}: {
  label: string;
  bytes: number;
  max: number;
  hint?: string;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="truncate font-medium">{label}</span>
        <span className="shrink-0 tabular-nums text-muted-foreground">
          {hint && <span className="mr-2 text-xs">{hint}</span>}
          {formatBytes(bytes)}
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary/70"
          style={{ width: `${max > 0 ? Math.max(1, (bytes / max) * 100) : 0}%` }}
        />
      </div>
    </div>
  );
}

function ModuleRow({
  module,
  max,
}: {
  module: UsageBreakdown["database"]["modules"][number];
  max: number;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-md px-1 py-1.5 text-left hover:bg-muted/60"
      >
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            !open && "-rotate-90",
          )}
        />
        <div className="min-w-0 flex-1">
          <UsageBar
            label={module.name}
            bytes={module.bytes}
            max={max}
            hint={`${module.items.toLocaleString("pt-BR")} registros`}
          />
        </div>
      </button>
      {open && (
        <ul className="mb-2 ml-6 divide-y rounded-md border text-xs">
          {module.tables.map((t) => (
            <li key={t.name} className="flex items-center justify-between gap-3 px-3 py-1.5">
              <span className="truncate font-mono text-muted-foreground">{t.name}</span>
              <span className="shrink-0 tabular-nums">
                <span className="mr-2 text-muted-foreground">
                  {t.rows.toLocaleString("pt-BR")} reg.
                </span>
                {formatBytes(t.bytes)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GaugeCard({
  icon: Icon,
  title,
  percent,
  primary,
  secondary,
}: {
  icon: LucideIcon;
  title: string;
  percent: number;
  primary: string;
  secondary: string;
}) {
  const lv = level(percent);
  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Icon className="h-4 w-4" /> {title}
          </span>
          <span className={cn("text-lg font-bold tabular-nums", TEXT_COLOR[lv])}>
            {Math.round(percent)}%
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div
            className={cn("h-full rounded-full transition-all duration-500", BAR_COLOR[lv])}
            style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
          />
        </div>
        <div>
          <p className="text-sm font-semibold tabular-nums">{primary}</p>
          <p className="text-xs text-muted-foreground">{secondary}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ServiceRow({ service, extra }: { service: ServiceCheck; extra?: string }) {
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      {service.ok ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
      ) : (
        <XCircle className="h-4 w-4 shrink-0 text-red-500" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{service.name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {service.ok
            ? [service.detail, extra].filter(Boolean).join(" · ") || "Respondendo"
            : `Fora do ar — ${service.detail ?? "sem resposta"}`}
        </p>
      </div>
      {service.latencyMs != null && (
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {service.latencyMs} ms
        </span>
      )}
    </li>
  );
}

function DetailRow({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b py-2 last:border-b-0">
      <div>
        <p className="text-muted-foreground">{label}</p>
        {hint && <p className="text-[11px] text-muted-foreground/70">{hint}</p>}
      </div>
      <div className="text-right font-medium">{value}</div>
    </div>
  );
}
